import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    CircularProgress,
    Divider,
    Box,
    Button
} from "@material-ui/core";
import { Helmet } from 'react-helmet-async';
import TopBar from '../common/TopBar';
import { useHistory } from 'react-router-dom'

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(3),
    },
    card: {
        marginBottom: theme.spacing(2),
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    chipContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        marginTop: theme.spacing(1),
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    pricingGrid: {
        marginTop: theme.spacing(1),
    },
    backButton: {
        marginBlock: theme.spacing(2),
        justifyContent: 'end',
        display: 'flex',
    }
}));

const PRODUCT_QUERY = gql`
    query Product($id: ID!) {
        Product(id: $id) {
            id
            code
            description
            category
            type
            style
            core
            mode
            species
            machined
            thickness
            direction
            finish
            frame
            firerated
            rosette
            radius
            width
            height
            length
            trimlength
            tracklength
            hook
            shelflength
            minwidth
            maxwidth
            size
            grade
            header
            supplier
            brand
            material
            depth
            design
            color
            drawer
            hardware
            accessory
            panelProfile
            sticking
            bifold
            combo
            backband
            uom
            pricingTiers {
                lp00 lp02 lp04 lp05 lp06 lp08 lp10 lp15
                sp1 sp2 sp3 sp4 sp5
                gb1 gb2
                wn3 wn4
                la3 la4
                rmg1 rmg2 rmg3
                tw dt hh
            }
        }
    }
`;

const Product = () => {
    const classes = useStyles();
    const { id } = useParams();
    const history = useHistory();
    const { data, loading, error } = useQuery(PRODUCT_QUERY, {
        variables: { id }
    });

    if (loading) {
        return <CircularProgress />
    }

    if (error) {
        return <div>Error loading product</div>
    }

    const product = data.Product;

    const renderAttribute = (label, value) => {
        if (!value || value === 'n/a' || value === '') return null;
        return (
            <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Typography variant="body2">
                    {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
                </Typography>
            </Grid>
        );
    };

    const renderPricingTier = (label, value) => {
        if (!value) return null;
        return (
            <Grid item xs={6} sm={4} md={3}>
                <Typography variant="caption" color="textSecondary">
                    {label}
                </Typography>
                <Typography variant="body2">
                    ${value.toFixed(2)}
                </Typography>
            </Grid>
        );
    };

    return (
        <div className={classes.root}>
            <Helmet>
                <title>Riverside Takeoffs :: Product Details</title>
            </Helmet>
            <TopBar isLoggedIn={true} title="Product Details"/>
            {/* Back button to the products list */}
            <div className={classes.backButton}>
                <Button variant="contained" color="primary" onClick={() => history.push('/products')}>
                    Back to Products
                </Button>
            </div>
            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="h4" gutterBottom>
                        {product.code}
                    </Typography>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        {product.description}
                    </Typography>
                    
                    <div className={classes.chipContainer}>
                        <Chip label={product.category} color="primary" className={classes.chip} />
                        {product.type && <Chip label={product.type} variant="outlined" className={classes.chip} />}
                        {product.uom && <Chip label={`UOM: ${product.uom}`} variant="outlined" className={classes.chip} />}
                    </div>
                </CardContent>
            </Card>

            <Card className={classes.card}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Product Attributes
                    </Typography>
                    <Divider />
                    <Box mt={2}>
                        <Grid container spacing={2}>
                            {renderAttribute('Style', product.style)}
                            {renderAttribute('Core', product.core)}
                            {renderAttribute('Mode', product.mode)}
                            {renderAttribute('Species', product.species)}
                            {renderAttribute('Machined', product.machined)}
                            {renderAttribute('Thickness', product.thickness)}
                            {renderAttribute('Direction', product.direction)}
                            {renderAttribute('Finish', product.finish)}
                            {renderAttribute('Frame', product.frame)}
                            {renderAttribute('Fire Rated', product.firerated)}
                            {renderAttribute('Rosette', product.rosette)}
                            {renderAttribute('Radius', product.radius)}
                            {renderAttribute('Width', product.width)}
                            {renderAttribute('Height', product.height)}
                            {renderAttribute('Length', product.length)}
                            {renderAttribute('Trim Length', product.trimlength)}
                            {renderAttribute('Track Length', product.tracklength)}
                            {renderAttribute('Hook', product.hook)}
                            {renderAttribute('Shelf Length', product.shelflength)}
                            {renderAttribute('Min Width', product.minwidth)}
                            {renderAttribute('Max Width', product.maxwidth)}
                            {renderAttribute('Size', product.size)}
                            {renderAttribute('Grade', product.grade)}
                            {renderAttribute('Header', product.header)}
                            {renderAttribute('Supplier', product.supplier)}
                            {renderAttribute('Brand', product.brand)}
                            {renderAttribute('Material', product.material)}
                            {renderAttribute('Depth', product.depth)}
                            {renderAttribute('Design', product.design)}
                            {renderAttribute('Color', product.color)}
                            {renderAttribute('Drawer', product.drawer)}
                            {renderAttribute('Hardware', product.hardware)}
                            {renderAttribute('Accessory', product.accessory)}
                            {renderAttribute('Panel Profile', product.panelProfile)}
                            {renderAttribute('Sticking', product.sticking)}
                            {renderAttribute('Bifold', product.bifold)}
                            {renderAttribute('Combo', product.combo)}
                            {renderAttribute('Backband', product.backband)}
                        </Grid>
                    </Box>
                </CardContent>
            </Card>

            {product.pricingTiers && (
                <Card className={classes.card}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Pricing Tiers
                        </Typography>
                        <Divider />
                        <Box mt={2}>
                            <Grid container spacing={2} className={classes.pricingGrid}>
                                {renderPricingTier('LP00', product.pricingTiers.lp00)}
                                {renderPricingTier('LP02', product.pricingTiers.lp02)}
                                {renderPricingTier('LP04', product.pricingTiers.lp04)}
                                {renderPricingTier('LP05', product.pricingTiers.lp05)}
                                {renderPricingTier('LP06', product.pricingTiers.lp06)}
                                {renderPricingTier('LP08', product.pricingTiers.lp08)}
                                {renderPricingTier('LP10', product.pricingTiers.lp10)}
                                {renderPricingTier('LP15', product.pricingTiers.lp15)}
                                {renderPricingTier('SP1', product.pricingTiers.sp1)}
                                {renderPricingTier('SP2', product.pricingTiers.sp2)}
                                {renderPricingTier('SP3', product.pricingTiers.sp3)}
                                {renderPricingTier('SP4', product.pricingTiers.sp4)}
                                {renderPricingTier('SP5', product.pricingTiers.sp5)}
                                {renderPricingTier('GB1', product.pricingTiers.gb1)}
                                {renderPricingTier('GB2', product.pricingTiers.gb2)}
                                {renderPricingTier('WN3', product.pricingTiers.wn3)}
                                {renderPricingTier('WN4', product.pricingTiers.wn4)}
                                {renderPricingTier('LA3', product.pricingTiers.la3)}
                                {renderPricingTier('LA4', product.pricingTiers.la4)}
                                {renderPricingTier('RMG1', product.pricingTiers.rmg1)}
                                {renderPricingTier('RMG2', product.pricingTiers.rmg2)}
                                {renderPricingTier('RMG3', product.pricingTiers.rmg3)}
                                {renderPricingTier('TW', product.pricingTiers.tw)}
                                {renderPricingTier('DT', product.pricingTiers.dt)}
                                {renderPricingTier('HH', product.pricingTiers.hh)}
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export default Product;
